// Components==============
import {
  faCheck,
  faExchange,
  faPlus,
  faTimes,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { AnimatePresence, motion } from 'framer-motion';
import { EditableProcessEntity } from 'hooks/useProcessGroups';
import { useRootModal } from 'hooks/useRootModal';
import { useTranslate } from 'hooks/useTranslate';
import { ProjectDetailsContext } from 'pages/Workspace/ProjectDetails/ProjectDetails';
import React, { useContext, useEffect, useState } from 'react';
import { framerMountAndScale } from 'utilities/FramerAnimations';
import AddActivityModal from '../AddActivityModal/AddActivityModal';
import Button from '../Button/Button';
import CircleButton from '../CircleButton/CircleButton';
import { DreamModalContext } from '../DreamModal/DreamModal';
import ProgressManager from '../DreamModal/ProgressManager/ProgressManager';
import Modal from '../Modal/Modal';
import { ProcessContext } from '../ProjectProcess/components/Process/Process';
import RichText from '../RichText/RichText';
import Tooltip from '../Tooltip/Tooltip';
import './Token.module.scss';
// =========================

export default function Token({
  token,
  process,
  handleAdd,
  handleSwitch,
  handleDelete,
  preventDeleteOrSwitch,
  canSwitch,
  isUsed,
  style,
  withMargin,
  isPhase,
  small,
  onbekend,
  mobileModal,
}: {
  token?: PhaseEntity | ActivityEntity;
  process?: EditableProcessEntity;
  handleAdd?: () => void;
  handleSwitch?: () => void;
  handleDelete?: () => void;
  preventDeleteOrSwitch?: boolean;
  canSwitch?: boolean;
  isUsed?: boolean;
  style?: React.CSSProperties;
  withMargin?: boolean;
  isPhase?: boolean;
  small?: boolean;
  onbekend?: boolean;
  mobileModal?: boolean;
}) {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { setModal } = useRootModal();
  const translate = useTranslate();
  const {
    setIsEditable,
    handleProcessSave,
    handleProcessSaveUnknownToken,
    handleSwitchProcessItem,
    setProcessGroups,
    processGroups,
  } = useContext(ProjectDetailsContext);
  const { phaseIndex, processIndex, editableProcess } = useContext(
    ProcessContext
  );

  const isEditable =
    (!!handleSwitch || !!handleAdd || !!handleDelete || canSwitch) &&
    token?.name !== 'Vertrekpunt' &&
    token?.name !== 'Starting point' &&
    token?.name !== 'Eindpunt' &&
    token?.name !== 'Endpoint';

  const { canDeletePhase } = useContext(ProcessContext);
  const { dream } = useContext(DreamModalContext);
  const inDreamModal = !!dream;

  const [needsSaving, setNeedsSaving] = useState(false);

  // Save process if unknown token is changed or accepted
  useEffect(() => {
    if (needsSaving) {
      console.log('save');
      handleProcessSaveUnknownToken(processGroups);
      setNeedsSaving(false);
    }
  }, [needsSaving]);

  return (
    <>
      <motion.div
        {...framerMountAndScale}
        styleName={`container ${isEditable ? 'permit-hover' : ''} ${
          isUsed ? 'is-used' : ''
        } ${withMargin ? 'with-margin' : ''} ${small ? 'small' : ''} ${
          inDreamModal ? 'in-dream-modal' : ''
        } ${mobileModal ? 'mobile' : ''}`}
      >
        <button
          type="button"
          styleName="wrapper"
          onClick={(e) => {
            e.stopPropagation();
            setModal(
              <Modal video={token?.video}>
                <div styleName="modal-content">
                  <h1>{token?.name}</h1>
                  <p styleName="summary">{token?.summary}</p>
                  <RichText content={token?.description} />
                </div>
              </Modal>
            );
          }}
        >
          {isUsed && (
            <div styleName="is-used">
              {translate(
                `project.already-in-use.${isPhase ? 'phase' : 'activity'}`
              )}
            </div>
          )}
          <img style={style} src={token?.thumbnail} alt={token?.name} />
          <ProgressManager />
          {isEditable && (
            <div styleName="overlay">
              {/* Tooltip if you can't delete item */}
              {!!handleDelete && !!preventDeleteOrSwitch && (
                <div styleName="delete" onClick={(e) => e.stopPropagation()}>
                  <Tooltip
                    content={translate(
                      `project.cant-delete.${
                        process?.process_type === 'App\\Phase'
                          ? 'phase'
                          : 'activity'
                      }`
                    )}
                  >
                    <CircleButton color="dark-grey" inactive icon={faTrash} />
                  </Tooltip>
                </div>
              )}
              {!!handleDelete && !preventDeleteOrSwitch && canDeletePhase && (
                <div styleName="delete">
                  <CircleButton
                    color={inDreamModal ? 'white-blue-shadow' : 'white-blue'}
                    icon={faTrash}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete();
                    }}
                  />
                </div>
              )}
              {!!handleAdd && (
                <div styleName="add-switch">
                  <CircleButton
                    color="white-blue"
                    icon={faPlus}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAdd();
                    }}
                  />
                </div>
              )}
              {!!handleSwitch && (
                <div
                  styleName="add-switch"
                  style={{ transform: 'rotate(-45deg)' }}
                >
                  <CircleButton
                    color="white-blue"
                    icon={faExchange}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSwitch();
                    }}
                  />
                </div>
              )}
              {canSwitch && !preventDeleteOrSwitch && (
                <div
                  styleName="add-switch"
                  style={{ transform: 'rotate(-45deg)' }}
                >
                  <CircleButton
                    color="white-blue"
                    icon={faExchange}
                    onClick={(e) => {
                      e.stopPropagation();
                      setModalIsOpen(true);
                    }}
                  />
                </div>
              )}
              {/* Tooltip if you can't switch item */}
              {canSwitch && preventDeleteOrSwitch && (
                <div styleName="switch" onClick={(e) => e.stopPropagation()}>
                  <Tooltip
                    content={translate(
                      `project.cant-switch.${
                        process?.process_type === 'App\\Phase'
                          ? 'phase'
                          : 'activity'
                      }`
                    )}
                  >
                    <CircleButton
                      color="dark-grey"
                      inactive
                      icon={faExchange}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          )}
          {mobileModal && (
            <div styleName="overlay">
              {!!handleSwitch && (
                <div
                  styleName="add-switch"
                  style={{ transform: 'rotate(-45deg)' }}
                >
                  <CircleButton
                    color="white-blue"
                    icon={faExchange}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSwitch();
                    }}
                  />
                </div>
              )}
            </div>
          )}
          {/* Overlay if Unknown token */}
          {onbekend && (
            <div styleName="overlay-onbekend">
              <div styleName="onbekend-top">
                <Button color="white">
                  {translate('project.onbekend-check')}
                </Button>
              </div>
              <div styleName="onbekend-left">
                {/* Button to confirm most reliable option */}
                <CircleButton
                  color="green"
                  icon={faCheck}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSwitchProcessItem(
                      process.process_type,
                      token,
                      phaseIndex,
                      processIndex,
                      onbekend
                    );
                    // Save if not in edit mode
                    if (!isEditable) setNeedsSaving(true);
                  }}
                />
              </div>
              <div styleName="onbekend-right">
                {/* Button to switch most reliable option by opening modal */}
                <CircleButton
                  color="red"
                  icon={faTimes}
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalIsOpen(true);
                  }}
                />
              </div>
            </div>
          )}
        </button>
      </motion.div>

      <AnimatePresence>
        {modalIsOpen && (
          <AddActivityModal
            permission={process?.process_type}
            fn="switch"
            origin="project"
            setModalIsOpen={setModalIsOpen}
            onbekend={onbekend}
            setNeedsSaving={setNeedsSaving}
          />
        )}
      </AnimatePresence>
    </>
  );
}
